import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Receipt } from 'lucide-react';
import { SalesTableComparativeStats } from './SalesTableComparativeStats';

interface SalesListTableProps {
  sales: any[];
  productById: Record<string, any>;
  statusFilter: string;
  onStatusFilterChange?: (filter: any) => void;
  hasAccess?: boolean;
  currentPage: number;
  rowsPerPage: number;
  totalPages: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rows: number) => void;
  showComparativeStats?: boolean;
}

export const SalesListTable: React.FC<SalesListTableProps> = ({
  sales,
  productById,
  statusFilter,
  onStatusFilterChange,
  hasAccess = true,
  currentPage,
  rowsPerPage,
  totalPages,
  onPageChange,
  onRowsPerPageChange,
  showComparativeStats = false
}) => {
  const formatPrice = (price: number) => 
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);

  const paginatedSales = React.useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return sales.slice(start, start + rowsPerPage);
  }, [sales, currentPage, rowsPerPage]);

  return (
    <div className="space-y-6">
      <Card className="transition-all duration-200 hover:-translate-y-1 hover:shadow-md flex flex-col max-h-[800px]">
        <CardHeader className="flex-none flex-row items-center justify-between space-y-0 pb-4 sticky top-0 z-10 bg-background border-b">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-gradient-to-br from-amber-500/10 to-orange-600/20 transition-transform duration-200 hover:scale-105">
              <Receipt className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium mb-1 text-amber-700 dark:text-amber-300">
                Ventes
              </p>
              <CardTitle className="text-xl font-bold text-amber-800 dark:text-amber-200">
                Liste des ventes
              </CardTitle>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <Select value={statusFilter} onValueChange={onStatusFilterChange}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="nouveau">Nouveau</SelectItem>
                <SelectItem value="best_seller">Best seller</SelectItem>
                <SelectItem value="en_promotion">En promotion</SelectItem>
                <SelectItem value="fin_de_serie">Fin de série</SelectItem>
                <SelectItem value="archive">Archivé</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-auto">
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="min-w-[600px] px-4 sm:px-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Produit</TableHead>
                    <TableHead>Quantité</TableHead>
                    <TableHead>Prix unitaire</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Mode de paiement</TableHead>
                    <TableHead>Vendeur</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedSales.slice(0, hasAccess ? paginatedSales.length : 10).map((s, index) => {
                    const isBlurred = !hasAccess && index >= 10;
                    const p = productById[s.product_id];
                    return (
                      <TableRow key={s.id} className={isBlurred ? 'blur-sm opacity-50 pointer-events-none' : ''}>
                        <TableCell>{new Date(s.sale_date).toLocaleString('fr-FR')}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{s.product_name}</span>
                            {p?.cycle_de_vie && <StatusBadge status={p.cycle_de_vie} />}
                          </div>
                          <div className="text-xs text-muted-foreground">CB: {p?.barcode ?? '—'}</div>
                        </TableCell>
                        <TableCell>{s.quantity_sold}</TableCell>
                        <TableCell>{formatPrice(s.unit_price)}</TableCell>
                        <TableCell>{formatPrice(s.total_amount)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {s.payment_method === 'Espèces' && '💰'}
                            {s.payment_method === 'Carte Bancaire' && '💳'}
                            {s.payment_method === 'Chèque' && '📄'}
                            {s.payment_method === 'Autre' && '❓'}
                            <span className="text-xs">{s.payment_method}</span>
                          </div>
                        </TableCell>
                        <TableCell>{s.seller_name || '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Message pour lignes masquées */}
          {!hasAccess && paginatedSales.length > 10 && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800 mb-1">
                <strong>{paginatedSales.length - 10}</strong> ligne{paginatedSales.length - 10 > 1 ? 's' : ''} masquée{paginatedSales.length - 10 > 1 ? 's' : ''} (mode démo)
              </p>
              <p className="text-xs text-amber-600">
                Passez à Premium pour voir toutes les ventes
              </p>
            </div>
          )}

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-4 gap-4">
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <span className="text-sm text-muted-foreground">Lignes par page :</span>
              <select 
                className="bg-background border rounded-md px-2 py-1 text-sm w-full sm:w-auto" 
                value={rowsPerPage} 
                onChange={e => onRowsPerPageChange?.(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="flex items-center justify-between sm:justify-end gap-2 text-sm w-full sm:w-auto">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onPageChange?.(currentPage - 1)} 
                disabled={currentPage === 1 || !hasAccess} 
                className="flex-1 sm:flex-none"
              >
                Précédent
              </Button>
              <span className="text-xs sm:text-sm whitespace-nowrap px-2">
                Page {currentPage} / {totalPages}
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onPageChange?.(currentPage + 1)} 
                disabled={currentPage === totalPages || !hasAccess} 
                className="flex-1 sm:flex-none"
              >
                Suivant
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {showComparativeStats && (
        <SalesTableComparativeStats sales={sales} customColor="hsl(32 95% 60%)" />
      )}
    </div>
  );
};